import _extends from "/usr/app/node_modules/@babel/runtime/helpers/esm/extends.js";
import _objectSpread from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import _slicedToArray from "/usr/app/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import React from "react";
var __jsx = React.createElement;
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "@i18n";
import { useLang } from "@/common/Contexts/LanguageContext";
import { twMerge } from "tailwind-merge";
import { CDN_LINK } from "@globalConfig";
import useApi from "@hooks/useApi";
import useButtonLoading from "@hooks/useButtonLoading";
import InputPhoneNumber from "@componentsShared/InputPhoneNumber/InputPhoneNumber";
import RHFCheckbox from "@/common/HOCs/rhf/checkbox/RHFCheckbox";
import { api__getCitiesDropDown } from "@/apis/assets/api__get_cities_drop_down";
import { useAuthActions, useSession } from "@/_MiniApps/auth/AuthContext";
import ChangeAccountVerificationModal from "@/_MiniApps/auth/ui/ChangeAccountVerificationModal/ChangeAccountVerificationModal";
import { useCreditCardCheckout } from "@/common/Contexts/CreditCardCheckoutContext";
import RiyalSymbol from "@componentsShared/InputCurrency/RiyalSymbol";
import { toast } from "sonner";
import { trackEvents } from "@/common/events/events";
var NAME_PATTERN = /^[؀-ۿa-zA-Z\s]+$/;
var schema = z.object({
  first_name: z.string().min(1, "validation_name_filled").regex(NAME_PATTERN, "validation_name_contain_symbols_unsupported_characters"),
  family_name: z.string().min(1, "validation_name_filled").regex(NAME_PATTERN, "validation_name_contain_symbols_unsupported_characters"),
  phone_number: z.string().min(1, "validation_mobile_number_chosen").regex(/^(05)[0-9]{8}$/, "validation_mobile_number_consist_ten_numbers"),
  city_id: z.string().min(1, "validation_city_chosen"),
  whatsapp_optin: z["boolean"]().optional()
});
var splitFullName = function splitFullName(fullName) {
  var parts = (fullName || "").trim().split(/\s+/).filter(Boolean);
  return {
    first_name: parts[0] || "",
    family_name: parts.slice(1).join(" ") || ""
  };
};

// credit-card has its OWN persist key (NOT the cash/shop-apply key) so warranty/city/name
// are independent per flow; context mount-restore reads the same key on credit-card pages.
var getStorageKey = function getStorageKey(postId) {
  return "credit_card_form_data_".concat(postId);
};
var readSession = function readSession(postId) {
  try {
    var raw = typeof sessionStorage !== "undefined" ? sessionStorage.getItem(getStorageKey(postId)) : null;
    return raw ? JSON.parse(raw) : {};
  } catch (_unused) {
    return {};
  }
};

// floating label chip sitting on the input top border (desktop only — matches Figma)
var FloatingLabel = function FloatingLabel(_ref) {
  var children = _ref.children;
  return /*#__PURE__*/React.createElement("span", {
    className: "hidden md:block absolute -top-2 right-3 bg-white px-1 text-[12px] font-semibold leading-none text-[#020202]"
  }, children);
};
var CreditCardForm = function CreditCardForm(_ref2) {
  var _ref3, _sessionData$first_na, _ref4, _sessionData$family_n, _formData$whatsapp_op, _checkoutOrderV2$post2, _checkoutOrderV2$deta2;
  var onComplete = _ref2.onComplete;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var lang = useLang();
  var buttonLoading = useButtonLoading();
  var _useSession = useSession(),
    isLoggedIn = _useSession.isLoggedIn,
    user = _useSession.user;
  var _useAuthActions = useAuthActions(),
    logout = _useAuthActions.logout;
  var _useState = useState(false),
    showVerificationModal = _useState[0],
    setShowVerificationModal = _useState[1];
  var _useCreditCardCheckou = useCreditCardCheckout().state,
    checkoutOrderV2 = _useCreditCardCheckou.checkoutOrderV2,
    formData = _useCreditCardCheckou.formData,
    fetching_error = _useCreditCardCheckou.fetching_error;
  var post_id = useCreditCardCheckout().queryParams.post_id;
  var setFormItem = useCreditCardCheckout().actions.setFormItem;
  var fetch__getOrderDetailsByParamsV2 = useCreditCardCheckout().remote_actions.fetch__getOrderDetailsByParamsV2;
  var _useApi = useApi([], api__getCitiesDropDown),
    _useApi2 = _slicedToArray(_useApi, 3),
    cities = _useApi2[2];
  // restore on back-navigation from Arboon: session → context → user
  var sessionData = readSession(post_id);
  var sessionNames = splitFullName(sessionData.full_name);
  var prefill = splitFullName((formData === null || formData === void 0 ? void 0 : formData.full_name) || (user === null || user === void 0 ? void 0 : user.full_name));
  var methods = useForm({
    mode: "onBlur",
    resolver: zodResolver(schema),
    defaultValues: {
      first_name: (_ref3 = (_sessionData$first_na = sessionData.first_name) !== null && _sessionData$first_na !== void 0 ? _sessionData$first_na : sessionNames.first_name) !== null && _ref3 !== void 0 ? _ref3 : prefill.first_name,
      family_name: (_ref4 = (_sessionData$family_n = sessionData.family_name) !== null && _sessionData$family_n !== void 0 ? _sessionData$family_n : sessionNames.family_name) !== null && _ref4 !== void 0 ? _ref4 : prefill.family_name,
      phone_number: (formData === null || formData === void 0 ? void 0 : formData.phone_number) || (user === null || user === void 0 ? void 0 : user.phone_number) || "",
      city_id: sessionData.city_id != null ? String(sessionData.city_id) : formData !== null && formData !== void 0 && formData.city_id ? String(formData.city_id) : "",
      whatsapp_optin: sessionData.whatsapp_optin != null ? Number(sessionData.whatsapp_optin) !== 0 : Number((_formData$whatsapp_op = formData === null || formData === void 0 ? void 0 : formData.whatsapp_optin) !== null && _formData$whatsapp_op !== void 0 ? _formData$whatsapp_op : 1) !== 0
    }
  });
  var control = methods.control,
    handleSubmit = methods.handleSubmit,
    getValues = methods.getValues,
    errors = methods.formState.errors;
  var err = function err(field) {
    var _errors$field;
    return (_errors$field = errors[field]) !== null && _errors$field !== void 0 && _errors$field.message ? t(errors[field].message, {
      ns: "web_checkout_cash"
    }) : "";
  };

  // SE: restore saved selections into the shared context on mount (provider remounts on back-nav,
  // wiping formData) so the CTA city/warranty and the create payload stay correct.
  useEffect(function () {
    var s = readSession(post_id);
    if (s.full_name != null) setFormItem("full_name", s.full_name);
    if (s.city_id != null) setFormItem("city_id", Number(s.city_id));
    if (s.warranty_id != null) setFormItem("warranty_id", Number(s.warranty_id));
  }, []);
  useEffect(function () {
    var _checkoutOrderV2$post;
    // only seed the city from the order when nothing was restored/selected yet
    if (!(formData !== null && formData !== void 0 && formData.city_id) && readSession(post_id).city_id == null && checkoutOrderV2 !== null && checkoutOrderV2 !== void 0 && (_checkoutOrderV2$post = checkoutOrderV2.post) !== null && _checkoutOrderV2$post !== void 0 && _checkoutOrderV2$post.city_id) {
      setFormItem("city_id", checkoutOrderV2.post.city_id);
      methods.setValue("city_id", String(checkoutOrderV2.post.city_id));
    }
  }, [checkoutOrderV2 === null || checkoutOrderV2 === void 0 ? void 0 : (_checkoutOrderV2$post2 = checkoutOrderV2.post) === null || _checkoutOrderV2$post2 === void 0 ? void 0 : _checkoutOrderV2$post2.city_id]);

  // persist form fields on every change so back-navigation from Arboon restores them
  useEffect(function () {
    var sub = methods.watch(function (values) {
      try {
        var _values$first_name, _values$family_name;
        if (typeof sessionStorage === "undefined") return;
        var key = getStorageKey(post_id);
        var existing = readSession(post_id);
        var first = (values.first_name || "").trim();
        var family = (values.family_name || "").trim();
        sessionStorage.setItem(key, JSON.stringify(_objectSpread(_objectSpread(_objectSpread({}, existing), {}, {
          first_name: (_values$first_name = values.first_name) !== null && _values$first_name !== void 0 ? _values$first_name : "",
          family_name: (_values$family_name = values.family_name) !== null && _values$family_name !== void 0 ? _values$family_name : "",
          full_name: [first, family].filter(Boolean).join(" ")
        }, values.city_id ? {
          city_id: values.city_id
        } : {}), {}, {
          whatsapp_optin: values.whatsapp_optin ? 1 : 0
        })));
      } catch (_unused2) {
        // sessionStorage full or unavailable
      }
    });
    return function () {
      return sub.unsubscribe();
    };
  }, [post_id]);

  // surface pricing fetch errors
  useEffect(function () {
    if (fetching_error !== null && fetching_error !== void 0 && fetching_error.message) toast.error(fetching_error.message, {
      position: "bottom-center"
    });
  }, [fetching_error]);
  var buildPayload = function buildPayload(data) {
    return {
      post_id: post_id,
      first_name: data.first_name.trim(),
      last_name: data.family_name.trim(),
      phone_number: data.phone_number,
      city_id: Number(data.city_id),
      whatsapp_optin: data.whatsapp_optin ? 1 : 0
    };
  };
  var onSubmit = handleSubmit(function (data) {
    var _checkoutOrderV2$g4_d, _checkoutOrderV2$deta;
    // shop-apply / amwal parity: a logged-in user entering a phone different from their
    // account must confirm, then re-verify the new number via OTP.
    if (isLoggedIn && (user === null || user === void 0 ? void 0 : user.phone_number) != data.phone_number && !showVerificationModal) {
      return setShowVerificationModal(true);
    }
    var g4 = checkoutOrderV2 === null || checkoutOrderV2 === void 0 ? void 0 : (_checkoutOrderV2$g4_d = checkoutOrderV2.g4_data) === null || _checkoutOrderV2$g4_d === void 0 ? void 0 : _checkoutOrderV2$g4_d.g4_data_layer;
    var selectedCity = (cities || []).find(function (c) {
      return String(c.id) === data.city_id;
    });
    trackEvents("credit_card_form_submit", {
      post_id: String(post_id),
      inventory_type: g4 === null || g4 === void 0 ? void 0 : g4.inventory_type,
      post_make: g4 === null || g4 === void 0 ? void 0 : g4.post_make,
      post_model: g4 === null || g4 === void 0 ? void 0 : g4.post_model,
      post_year: g4 === null || g4 === void 0 ? void 0 : g4.post_year,
      post_condition: g4 === null || g4 === void 0 ? void 0 : g4.post_condition,
      post_price: g4 === null || g4 === void 0 ? void 0 : g4.post_price,
      total_amount: checkoutOrderV2 === null || checkoutOrderV2 === void 0 ? void 0 : (_checkoutOrderV2$deta = checkoutOrderV2.detail) === null || _checkoutOrderV2$deta === void 0 ? void 0 : _checkoutOrderV2$deta.total_amount,
      user_city: selectedCity === null || selectedCity === void 0 ? void 0 : selectedCity.name,
      platform: "web",
      referral_location: "pre_checkout"
    });
    // not logged in → OTP first; logged-in with own number → straight through.
    // (Order is created by the page after OTP — the create endpoint needs auth.)
    onComplete(buildPayload(data), !isLoggedIn);
  });
  var baseInput = "w-full p-3 rounded bg-white border border-solid border-[#d6dfe9] text-[14px] md:text-[16px] text-gray-text leading-6 placeholder:text-[#6c7a8d] md:placeholder:text-[#838383] outline-none focus:border-primary-new";
  var nameInput = twMerge(baseInput, "md:border-[1.5px]");
  var errorCls = "errorMessage text-[#F22C2C] text-xs font-medium leading-4 mt-1";
  return /*#__PURE__*/React.createElement(FormProvider, methods, /*#__PURE__*/React.createElement("form", {
    onSubmit: onSubmit,
    noValidate: true,
    autoComplete: "off",
    className: "flex flex-col gap-5 md:gap-6"
  }, /*#__PURE__*/React.createElement("strong", {
    className: "py-5 text-start text-[#484848] text-[16px] md:text-[20px] font-bold leading-8"
  }, t("add_information", {
    ns: "web_checkout_cash"
  })), /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-1"
  }, /*#__PURE__*/React.createElement("div", {
    className: "relative"
  }, /*#__PURE__*/React.createElement(FloatingLabel, null, t("first_name", {
    ns: "web_checkout_cash"
  })), /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "first_name",
    render: function render(_ref5) {
      var field = _ref5.field;
      return /*#__PURE__*/React.createElement("input", _extends({}, field, {
        id: "credit-card-firstname-input",
        type: "text",
        placeholder: t("first_name", {
          ns: "web_checkout_cash"
        }),
        className: twMerge(nameInput, errors.first_name && "border-[#F22C2C]")
      }));
    }
  })), err("first_name") && /*#__PURE__*/React.createElement("span", {
    className: errorCls
  }, err("first_name"))), /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-1"
  }, /*#__PURE__*/React.createElement("div", {
    className: "relative"
  }, /*#__PURE__*/React.createElement(FloatingLabel, null, t("family_name", {
    ns: "web_checkout_cash"
  })), /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "family_name",
    render: function render(_ref6) {
      var field = _ref6.field;
      return /*#__PURE__*/React.createElement("input", _extends({}, field, {
        id: "credit-card-familyname-input",
        type: "text",
        placeholder: t("family_name", {
          ns: "web_checkout_cash"
        }),
        className: twMerge(nameInput, errors.family_name && "border-[#F22C2C]")
      }));
    }
  })), err("family_name") && /*#__PURE__*/React.createElement("span", {
    className: errorCls
  }, err("family_name"))), /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-1"
  }, /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "phone_number",
    render: function render(_ref7) {
      var _ref7$field = _ref7.field,
        _onChange = _ref7$field.onChange,
        value = _ref7$field.value,
        onBlur = _ref7$field.onBlur;
      return /*#__PURE__*/React.createElement(InputPhoneNumber, {
        id: "credit-card-phone-input",
        defaultValue: value,
        onChange: function onChange(nextValue) {
          _onChange(nextValue);
          setFormItem("phone_number", nextValue);
        },
        onBlur: onBlur,
        errorLabel: err("phone_number"),
        errorLabelClassName: "!text-[#F22C2C] !m-1",
        direction: lang == "ar" ? "rtl" : "ltr"
      });
    }
  })), /*#__PURE__*/React.createElement("label", {
    id: "credit-card-whatsapp-label",
    className: "flex items-center cursor-pointer [&>*]:mx-[3px] [&>img]:w-6 [&>span]:text-[#6c7a8d] [&>span]:text-xs [&>span]:leading-6"
  }, /*#__PURE__*/React.createElement(RHFCheckbox, {
    name: "whatsapp_optin",
    defaultChecked: true
  }), /*#__PURE__*/React.createElement("img", {
    id: "credit-card-whatsapp-icon",
    src: CDN_LINK + "/assets/images/wpImg.svg",
    alt: "whts",
    width: "24px",
    height: "24px"
  }), /*#__PURE__*/React.createElement("span", {
    id: "credit-card-whatsapp-text"
  }, t("like_receive_updates_order_via", {
    ns: "web_checkout_finance"
  }))), /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-1"
  }, /*#__PURE__*/React.createElement("div", {
    className: "relative"
  }, /*#__PURE__*/React.createElement(FloatingLabel, null, t("city", {
    ns: "web_checkout_cash"
  })), /*#__PURE__*/React.createElement("img", {
    src: CDN_LINK + "/assets/images/down-arrow.svg",
    alt: "",
    width: 16,
    height: 16,
    className: "pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
  }), /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "city_id",
    render: function render(_ref8) {
      var field = _ref8.field;
      return /*#__PURE__*/React.createElement("select", _extends({}, field, {
        id: "credit-card-city-select",
        className: twMerge(baseInput, "appearance-none", !field.value && "text-[#6c7a8d]", errors.city_id && "border-[#F22C2C]"),
        onChange: function onChange(e) {
          field.onChange(e);
          var value = e.target.value;
          setFormItem("city_id", Number(value));
          // keep selected warranty across city change (backend re-validates eligibility)
          fetch__getOrderDetailsByParamsV2(post_id, _objectSpread({
            city_id: Number(value)
          }, (formData === null || formData === void 0 ? void 0 : formData.warranty_id) != null && {
            warranty_id: formData.warranty_id
          }));
        }
      }), /*#__PURE__*/React.createElement("option", {
        value: "",
        disabled: true
      }, t("city", {
        ns: "web_checkout_cash"
      })), (cities || []).map(function (city) {
        return /*#__PURE__*/React.createElement("option", {
          key: city.id,
          value: city.id
        }, city.name);
      }));
    }
  })), err("city_id") && /*#__PURE__*/React.createElement("span", {
    className: errorCls
  }, err("city_id"))), /*#__PURE__*/React.createElement("div", {
    className: "flex md:justify-end"
  }, /*#__PURE__*/React.createElement("button", {
    id: "credit-card-submit-btn",
    type: "submit",
    disabled: buttonLoading,
    className: "border-none flex items-center justify-center gap-2 w-full md:w-[361px] h-14 px-3 py-4 rounded bg-[#00b362] disabled:opacity-60"
  }, /*#__PURE__*/React.createElement("span", {
    className: "flex-1 text-start text-white text-base font-bold leading-6"
  }, t("continue_down_payment", {
    ns: "web_checkout_cash"
  })), /*#__PURE__*/React.createElement("span", {
    className: "flex items-center gap-1 text-white text-base font-bold leading-6"
  }, checkoutOrderV2 === null || checkoutOrderV2 === void 0 ? void 0 : (_checkoutOrderV2$deta2 = checkoutOrderV2.detail) === null || _checkoutOrderV2$deta2 === void 0 ? void 0 : _checkoutOrderV2$deta2.arboun, /*#__PURE__*/React.createElement(RiyalSymbol, {
    className: "text-white text-base font-bold leading-6"
  }))))), /*#__PURE__*/React.createElement(ChangeAccountVerificationModal, {
    show: showVerificationModal,
    onClose: function onClose() {
      return setShowVerificationModal(false);
    },
    onSubmit: function onSubmit() {
      setShowVerificationModal(false);
      // log out the old account, then verify the new number via OTP before creating the order
      logout(null).then(function () {
        return onComplete(buildPayload(getValues()), true);
      });
    },
    newPhoneNumber: getValues("phone_number"),
    loading: buttonLoading
  }));
};
export default CreditCardForm;